/**
 * Server-based clients API (REST).
 */

import { authenticatedHttpClient } from './httpClient';
import { SERVER_API_V2 } from '../config/apiBase';
import type { Client } from './clients';

export interface ClientListResponse {
  clients: Client[];
  hasMore: boolean;
}

export async function listClientsServer(
  organizationId: string,
  options?: { q?: string; limit?: number; offset?: number }
): Promise<ClientListResponse> {
  const params = new URLSearchParams();
  if (options?.q?.trim()) {
    params.set('q', options.q.trim());
  }
  if (options?.limit != null) {
    params.set('limit', String(options.limit));
  }
  if (options?.offset != null) {
    params.set('offset', String(options.offset));
  }
  const qs = params.toString();
  return authenticatedHttpClient.get<ClientListResponse>(
    `/clients/${organizationId}${qs ? `?${qs}` : ''}`,
    { baseURL: SERVER_API_V2 }
  );
}

export async function getClientServer(
  organizationId: string,
  clientId: string
): Promise<Client> {
  return authenticatedHttpClient.get<Client>(
    `/clients/${organizationId}/${clientId}`
  );
}

export async function createClientServer(
  organizationId: string,
  data: Record<string, unknown>
): Promise<Client> {
  return authenticatedHttpClient.post<Client>(
    `/clients/${organizationId}`,
    data
  );
}

export async function updateClientServer(
  organizationId: string,
  clientId: string,
  data: Record<string, unknown>
): Promise<Client> {
  return authenticatedHttpClient.put<Client>(
    `/clients/${organizationId}/${clientId}`,
    data
  );
}

export async function deleteClientServer(
  organizationId: string,
  clientId: string
): Promise<void> {
  await authenticatedHttpClient.delete(`/clients/${organizationId}/${clientId}`);
}

export interface ClientAppUser {
  id: string;
  uid: string;
  email: string;
  displayName?: string | null;
  role: string;
  clientId?: string | null;
  clientCode?: string | null;
  organizationIds?: string[];
  active?: boolean;
  phone?: string | null;
}

export async function listClientAppUsers(
  organizationId: string,
  clientId: string
): Promise<ClientAppUser[]> {
  return authenticatedHttpClient.get<ClientAppUser[]>(
    `/clients/${organizationId}/${clientId}/app-users`
  );
}

export async function inviteClientAppUser(
  organizationId: string,
  clientId: string,
  payload: {
    email: string;
    password: string;
    displayName: string;
    phone?: string;
  }
): Promise<ClientAppUser> {
  return authenticatedHttpClient.post<ClientAppUser>(
    `/clients/${organizationId}/${clientId}/app-users`,
    payload
  );
}
